import { Utils } from '../common';
import {
	ModelFilterRule,
	ModelFilterRuleFunction,
	ModelComplexFilterRule,
	FilterExpressionSimple
} from '../types';
import Modeller from './model-utils';

/**
 * FilterEvaluator - Model 层过滤器求值器
 *
 * 完全独立于 Widget 接口，可在任何有 root/model 的场景使用
 *
 * @example
 * // 静态方法调用
 * const result = FilterEvaluator.evaluate(
 *   { root, model },
 *   { on: 'status=1' }
 * );
 *
 * // 批量过滤
 * const filtered = items.filter(item =>
 *   new FilterEvaluator({ root, model: item }).evaluate(filterConfig)
 * );
 */
class FilterEvaluator {
	private root: any;
	private model: any;
	private arrayHolder?: any;

	constructor(context: { root: any; model: any; arrayHolder?: any }) {
		this.root = context.root;
		this.model = context.model;
		this.arrayHolder = context.arrayHolder;
	}

	/**
	 * 评估过滤器是否匹配
	 */
	evaluate(filterable: { on?: ModelFilterRule | ModelFilterRule[] }): boolean {
		const { on } = filterable;
		if (on == null) {
			return true;
		}
		return this.evaluateRules(Utils.toArray(on), 'and');
	}

	/**
	 * 评估规则数组
	 */
	evaluateRules(rules: ModelFilterRule[], relation: 'and' | 'or' = 'and'): boolean {
		if (rules.length === 0) {
			return true;
		}
		if (relation === 'and') {
			return !rules.some(rule => !this.evaluateSingleRule(rule));
		} else {
			return rules.some(rule => this.evaluateSingleRule(rule));
		}
	}

	/**
	 * 评估单个规则
	 */
	evaluateSingleRule(rule: ModelFilterRule): boolean {
		if (rule == null) {
			return true;
		} else if (rule === '--d') {
			return true;
		} else if (Utils.isString(rule)) {
			return this.evaluateStringRule(rule);
		} else if (Utils.isFunction(rule)) {
			return this.evaluateFunctionRule(rule as ModelFilterRuleFunction);
		} else if ((rule as ModelComplexFilterRule).and) {
			const subRules = Utils.toArray((rule as ModelComplexFilterRule).and);
			return this.evaluateRules(subRules, 'and');
		} else if ((rule as ModelComplexFilterRule).or) {
			const subRules = Utils.toArray((rule as ModelComplexFilterRule).or);
			return this.evaluateRules(subRules, 'or');
		} else {
			console.error('[FilterEvaluator] Unsupported rule type:', rule);
			return false;
		}
	}

	private evaluateStringRule(rule: string): boolean {
		const exp = Modeller.parseFilter(rule);

		if (Modeller.isSimpleExpression(exp) && Modeller.isFromRoot(exp.prop)) {
			return Modeller.matchFilter(this.root, {
				prop: Modeller.getRealId(exp.prop),
				operator: exp.operator,
				value: exp.value
			} as FilterExpressionSimple);
		} else {
			return Modeller.matchFilter(this.model, exp);
		}
	}

	private evaluateFunctionRule(rule: ModelFilterRuleFunction): boolean {
		return rule({
			root: this.root,
			model: this.model,
			arrayHolder: this.arrayHolder
		});
	}

	// ========== 静态便捷方法 ==========

	static evaluate(
		context: { root: any; model: any; arrayHolder?: any },
		filterable: { on?: ModelFilterRule | ModelFilterRule[] }
	): boolean {
		return new FilterEvaluator(context).evaluate(filterable);
	}

	static evaluateRules(
		context: { root: any; model: any; arrayHolder?: any },
		rules: ModelFilterRule[],
		relation: 'and' | 'or' = 'and'
	): boolean {
		return new FilterEvaluator(context).evaluateRules(rules, relation);
	}
}

export default FilterEvaluator;
